import { useEffect, useState } from "react";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";

import { TemplatePageType } from "models";
import {
  TEMPLATE_GALLERY,
  TemplateGalleryItem,
  coverPreviewHtml,
  normalizeTemplateContent,
} from "utils/templates";
import { useAppDispatch, useAppSelector, applyCoverDesign } from "utils/store";
import { saveTemplate } from "utils/api";

type Props = {
  visible: boolean;
  onHide: () => void;
};

const coverContentOf = (pages: { type: TemplatePageType; content?: unknown }[]) =>
  pages.find((p) => p.type === TemplatePageType.coverPage)?.content ?? [];

type CardProps = {
  item: TemplateGalleryItem;
  storageToken: string;
  applying: boolean;
  onUse: (item: TemplateGalleryItem) => void;
};

const GalleryCard = ({ item, storageToken, applying, onUse }: CardProps) => {
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    let active = true;
    item
      .load()
      .then((template) => {
        if (active) setPreviewHtml(coverPreviewHtml(coverContentOf(template.pages), storageToken));
      })
      .catch(() => active && setPreviewHtml(null));
    return () => {
      active = false;
    };
  }, [item, storageToken]);

  // Reveal "Use design" on hover (or while applying) so the grid stays calm.
  const showAction = hovered || applying;

  return (
    <div
      className="flex cursor-pointer flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all duration-200 ease-out hover:-translate-y-1 hover:border-[var(--ion-color-primary)] hover:shadow-lg"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onUse(item)}
    >
      {/* The page preview sits on a tinted mat so it reads as a sheet of paper, distinct from the card behind it. */}
      <div className="flex items-center justify-center bg-slate-100 p-4">
        <div
          className="relative w-full overflow-hidden bg-white shadow-md ring-1 ring-black/5"
          style={{ aspectRatio: "210 / 297" }}
        >
          {previewHtml ? (
            <div
              className="pointer-events-none h-full w-full overflow-hidden"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-400">
              <span className="text-xs">Loading…</span>
            </div>
          )}

          {/* Hover overlay with the action so the resting grid stays uncluttered. */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-150 ease-out"
            style={{
              background: "rgba(27, 20, 75, 0.55)",
              opacity: showAction ? 1 : 0,
            }}
          >
            <Button
              label="Use this design"
              size="small"
              loading={applying}
              className="border-none ring-2 ring-white/90 transition-colors duration-150"
              style={{ background: "var(--ion-color-primary)", color: "#ffffff" }}
            />
          </div>
        </div>
      </div>

      {/* Card note: metadata about the design, styled apart from the page preview above so it doesn't read as part of the template. */}
      <div className="flex-1 border-t border-slate-100 bg-slate-50 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--ion-color-secondary)]">
          Cover template
        </div>
        <div className="mt-1 text-sm font-semibold text-[var(--ion-color-primary)]">{item.name}</div>
        <div className="mt-0.5 text-xs text-slate-500">{item.description}</div>
      </div>
    </div>
  );
};

/**
 * Lets the user replace/restart just the cover page design from the gallery,
 * without affecting any other section. Picking a design asks for confirmation
 * (it discards the current cover edits), then normalizes the chosen cover
 * content and applies it.
 */
export const CoverDesignPicker = ({ visible, onHide }: Props) => {
  const dispatch = useAppDispatch();
  const storageToken = useAppSelector((state) => state.templateBuilderSlice.storageToken);
  const [applyingId, setApplyingId] = useState<string | null>(null);

  const applyDesign = async (item: TemplateGalleryItem) => {
    setApplyingId(item.id);
    try {
      const template = await item.load();
      const content = normalizeTemplateContent(coverContentOf(template.pages)) as any;
      dispatch(applyCoverDesign({ content }));
      dispatch(saveTemplate());
      onHide();
    } finally {
      setApplyingId(null);
    }
  };

  const confirmApply = (item: TemplateGalleryItem) => {
    confirmDialog({
      header: `Use the “${item.name}” cover design?`,
      message: "This replaces your current cover page design. Your other sections won’t be affected.",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Replace cover",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      defaultFocus: "reject",
      accept: () => applyDesign(item),
    });
  };

  return (
    <Dialog
      header={<span className="text-[var(--ion-color-primary)]">Choose a cover design</span>}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(880px, 94vw)" }}
      dismissableMask
    >
      <p className="mt-0 mb-4 text-slate-500">
        Only the cover changes. Every other section stays exactly as it is.
      </p>

      <div className="grid grid-cols-3 gap-5">
        {TEMPLATE_GALLERY.map((item) => (
          <GalleryCard
            key={item.id}
            item={item}
            storageToken={storageToken}
            applying={applyingId === item.id}
            onUse={confirmApply}
          />
        ))}
      </div>
    </Dialog>
  );
};
